/**
 * The one item tooltip — a floating card describing a single item.
 *
 * ─── Why one module and not two ────────────────────────────────────────────
 * Two surfaces need this: the inventory slot grid (`paperdoll.ts`) and the
 * vendor's buy/sell tiles (`vendor-ui.ts`). Neither could show anything at all
 * before, because the wire types carried name/kind/weight and nothing else —
 * every mechanical column was projected solely for EQUIPPED pieces. Widening
 * the two payloads separately would have produced two half-overlapping shapes
 * and two renderers that drift on the day a stat is added. Both now carry the
 * SAME `ItemStatsSummary` (backend `game/inventory/item-stats.ts`), and this
 * is the single thing that reads it.
 *
 * ─── Floating, and NOT inside the panel ────────────────────────────────────
 * A tooltip parented to its slot is clipped by the grid's own
 * `overflow-y: auto`, so the card for a bottom-row item would be cut in half.
 * `ItemTooltip` therefore mounts ONE element and moves it, rather than
 * rendering a card per cell — hovering 30 slots builds 1 DOM subtree, not 30.
 *
 * It mounts on `document.body` BY DEFAULT, and that default is load-bearing.
 * `z-index` resolves inside the nearest stacking context, and every panel that
 * wants a tooltip is one: `.bv-paperdoll` is `position:absolute; z-index:15`
 * and `#vendor-ui` is `position:fixed; z-index:1001`. Appending the card to
 * either caps it at that parent's level — the inventory card would paint at
 * 15, i.e. UNDER the panel scrims (1001) and under `.bv-hud` (1002), so
 * hovering a slot on the left of the screen would put the card behind the
 * player frame. `document.body` has no stacking context, so 1200 means 1200.
 *
 * Positioning is viewport-clamped and side-flipping: the card prefers the
 * pointer's right, and flips left when it would overflow. Without the flip the
 * rightmost column's tooltips render off-screen, which is exactly the column a
 * player reaches for last and notices least.
 *
 * `title` attributes are deliberately NOT kept alongside this. The native
 * tooltip appears after its own ~1s OS delay, in its own OS styling, on top of
 * this card — two tooltips for one hover.
 */
import type { ConsumableEffect, InventoryItemSummary, ItemStatsSummary } from '../../state/game-state.types';
import { ammoLabel, formatStatMods } from './stat-format';

/** Escapes text landing in an HTML string. Same rule as paperdoll.ts's copy. */
function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]!);
}

/**
 * `.item-flag` rarity modifiers, from app.css. Only COMMON/UNCOMMON/RARE are
 * styled, because only those three exist in `ItemRarity` (schema.prisma) — an
 * unknown string falls back to the base class rather than guessing a tier,
 * which is what that stylesheet's own comment asks for.
 */
const RARITY_CLASS: Record<string, string> = {
  COMMON: 'item-flag-common',
  UNCOMMON: 'item-flag-uncommon',
  RARE: 'item-flag-rare',
};

/** One `label: value` row. */
function row(label: string, value: string, cls = ''): string {
  return `<div class="bv-tip-row${cls ? ` ${cls}` : ''}"><span>${esc(label)}</span><span>${esc(value)}</span></div>`;
}

export interface ItemTooltipInput {
  name: string;
  kind: string;
  stats: ItemStatsSummary;
  /** Units in the hovered row, when it is an owned stack. Omitted for catalog entries. */
  quantity?: number;
  /** A price line under the stats — "Sells for 12 Cash" / "12 Cash each". */
  priceLine?: string;
  /** A price line with a coin in it (ready-made, caller-escaped HTML). Wins over `priceLine`. */
  priceLineHtml?: string;
  /**
   * Shown above the stats in the warn colour — why this item cannot be used
   * right now ("Hideout is full", "Requires STR 6"). Kept separate from the
   * stat rows so a blocked item reads as blocked, not as having an odd stat.
   */
  blockedReason?: string;
  /** Pre-built `<button>` markup for a PINNED card (click a slot). Hover cards carry none. */
  actionsHtml?: string;
}

/**
 * The card's inner HTML. PURE — no DOM, no state — so a test can assert on the
 * exact text a given item produces without mounting anything.
 *
 * Sections are emitted only when they have content: an item with no armour,
 * no damage, no mods and no requirements renders as a header, a kind line and
 * its weight, rather than as a card full of dashes.
 */
/**
 * What using an item does, in the player's words: "Heals 50 HP", "+25% HP
 * regen for 60 min", "+30 energy". One wording for the tooltip and the use
 * receipt, so the two cannot disagree.
 */
export function consumableEffectText(fx: ConsumableEffect): string {
  const min = (ms: number) => Math.round(ms / 60_000);
  const pct = (mult: number) => `+${Math.round((mult - 1) * 100)}%`;
  switch (fx.kind) {
    case 'HP_RESTORE': return `Heals ${fx.amount} HP`;
    case 'ENERGY_RESTORE': return `+${fx.amount} energy`;
    case 'HP_REGEN_BOOST': return `${pct(fx.mult)} HP regen for ${min(fx.durationMs)} min`;
    case 'ENERGY_BOOST': return `${pct(fx.mult ?? 2)} energy regen for ${min(fx.durationMs)} min`;
    case 'STAT_BUFF': {
      const code = ({ strength: 'STR', intellect: 'INT', stamina: 'STA', agility: 'AGI', perception: 'PER' } as const)[fx.attribute];
      return `+${fx.amount} ${code} for ${min(fx.durationMs)} min`;
    }
    default: return 'Usable';
  }
}

export function itemTooltipHtml(input: ItemTooltipInput): string {
  const { name, kind, stats } = input;
  const parts: string[] = [];

  const flagCls = RARITY_CLASS[stats.rarity] ?? '';
  parts.push(
    '<div class="bv-tip-head">'
    + `<span class="bv-tip-name">${esc(name)}</span>`
    + `<span class="item-flag ${flagCls}">${esc(stats.rarity.toLowerCase())}</span>`
    + '</div>',
  );

  const kindLine = stats.slot ? `${kind.toLowerCase()} · ${stats.slot.toLowerCase()}` : kind.toLowerCase();
  parts.push(`<div class="bv-tip-kind">${esc(kindLine)}</div>`);

  if (input.blockedReason) {
    parts.push(`<div class="bv-tip-blocked">${esc(input.blockedReason)}</div>`);
  }

  const body: string[] = [];
  if (stats.armorPoints != null) {
    const cls = stats.armorClass ? ` (${stats.armorClass.toLowerCase()})` : '';
    body.push(row('Armour', `${stats.armorPoints} AR${cls}`, 'bv-tip-row-armor'));
  }
  if (stats.baseDamage != null) body.push(row('Damage', `+${stats.baseDamage} power`, 'bv-tip-row-dmg'));
  if (stats.fixedDamage != null) body.push(row('Damage', `${stats.fixedDamage} fixed`, 'bv-tip-row-dmg'));
  if (stats.ammoType) body.push(row('Ammo', ammoLabel(stats.ammoType)));

  // Player-facing names and units (`stat-format.ts`), never the raw keys.
  for (const m of formatStatMods(stats.statMods)) {
    body.push(row(m.label, m.value, m.good ? 'bv-tip-row-up' : 'bv-tip-row-down'));
  }
  // What using it does. Before 2026-10-16 nothing said so, and 42 of the 51
  // consumables did nothing at all.
  const fx = stats.effect;
  if (fx) body.push(row('Use', consumableEffectText(fx), 'bv-tip-row-up'));
  // 2026-10-19: ordinary meds stopped working ("Keep med items, but they are
  // not usable"). Say so, or a bandage with no Use button reads as a bug.
  // `null` only: an older server omits the field, which says nothing.
  else if (kind === 'CONSUMABLE' && fx === null) body.push(row('Use', 'Not usable · trade and quest item', 'bv-tip-row-down'));
  if (body.length > 0) parts.push(`<div class="bv-tip-body">${body.join('')}</div>`);

  const req = Object.entries(stats.attrReq ?? {});
  if (req.length > 0) {
    // Requirements are a gate, not a stat — they get their own line in the
    // warn colour so "you cannot wear this yet" is not buried in the list of
    // things it would give you if you could.
    const text = req.map(([code, n]) => `${code} ${n}`).join(' · ');
    parts.push(`<div class="bv-tip-req">Requires ${esc(text)}</div>`);
  }

  const foot: string[] = [];
  // The item's UNIT weight, and — for a stack — the row's total beside it. The
  // wire carries both for exactly this reason (see ItemStatsSummary).
  const q = input.quantity ?? 1;
  foot.push(q > 1
    ? `${stats.weight.toFixed(2)} kg each · ${(stats.weight * q).toFixed(1)} kg total`
    : `${stats.weight.toFixed(1)} kg`);
  if (stats.maxDurability > 0 && (stats.slot || stats.armorPoints != null)) {
    foot.push(`${stats.maxDurability} durability`);
  }
  parts.push(`<div class="bv-tip-foot">${esc(foot.join(' · '))}</div>`);

  if (input.priceLineHtml) parts.push(`<div class="bv-tip-price">${input.priceLineHtml}</div>`);
  else if (input.priceLine) parts.push(`<div class="bv-tip-price">${esc(input.priceLine)}</div>`);
  if (input.actionsHtml) parts.push(`<div class="bv-tip-actions">${input.actionsHtml}</div>`);

  return parts.join('');
}

/** Convenience wrapper for the common case — an owned inventory row. */
export function inventoryTooltipHtml(
  item: InventoryItemSummary,
  extra: Omit<ItemTooltipInput, 'name' | 'kind' | 'stats' | 'quantity'> = {},
): string {
  return itemTooltipHtml({
    name: item.name, kind: item.kind, stats: item.stats, quantity: item.quantity, ...extra,
  });
}

/** Distance from the pointer, so the card never sits under the cursor itself. */
const OFFSET = 14;
/** Keep this much clear of the viewport edge. */
const EDGE = 8;

/**
 * One floating tooltip element, shared by every slot in a panel.
 *
 * Mount once per panel, call `show()` on pointerenter and `hide()` on
 * pointerleave. `move()` repositions without rebuilding the content, so
 * tracking the pointer across a slot costs two style writes per event.
 */
export class ItemTooltip {
  readonly el: HTMLDivElement;
  private visible = false;

  /**
   * @param parent Override only with an element that is NOT a stacking context
   *   — see the module docstring. The default is right for every current use.
   */
  constructor(parent: HTMLElement = document.body) {
    this.el = document.createElement('div');
    this.el.className = 'bv-tip';
    this.el.hidden = true;
    // Never a pointer target: the card follows the cursor, so an interactive
    // card would sit under the pointer, fire its own pointerleave on the slot
    // behind it, and flicker the tooltip off and on forever.
    this.el.setAttribute('role', 'tooltip');
    parent.appendChild(this.el);
  }

  show(html: string, x: number, y: number): void {
    this.el.innerHTML = html;
    this.el.hidden = false;
    this.visible = true;
    this.move(x, y);
  }

  move(x: number, y: number): void {
    if (!this.visible) return;
    // Measured AFTER the content is in and the element is visible — a hidden
    // element reports 0x0, which would place every card at the same corner.
    const w = this.el.offsetWidth;
    const h = this.el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Prefer the pointer's right; flip to its left when the card would run off
    // the edge. Without the flip the rightmost column is unreadable.
    let left = x + OFFSET;
    if (left + w > vw - EDGE) left = x - OFFSET - w;
    if (left < EDGE) left = EDGE;
    let top = y + OFFSET;
    if (top + h > vh - EDGE) top = Math.max(EDGE, vh - EDGE - h);
    this.el.style.left = `${left}px`;
    this.el.style.top = `${top}px`;
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.el.hidden = true;
    // Dropped so a stale card is never flashed by a `hidden = false` that
    // beats the next `show()`'s content write.
    this.el.innerHTML = '';
  }

  get isVisible(): boolean { return this.visible; }

  dispose(): void {
    this.el.remove();
  }
}
